/* ------------------------------------------------------------------
   تاریخ و زمان — سند مادر بند ۱۱.۱ و معیار AC-12
   ------------------------------------------------------------------
   قاعده طلایی:
     ذخیره‌سازی همیشه ISO-8601 UTC است.
     تاریخ شمسی فقط لایه نمایش است و هرگز به Backend فرستاده نمی‌شود.

   تمام ورودی‌های این ماژول رشته ISO/UTC یا Date هستند و خروجی رشته
   قابل نمایش فارسی است. تبدیل معکوس فقط در `jalaliToUtcIso` انجام
   می‌شود که خروجی‌اش دوباره ISO/UTC است.
   ------------------------------------------------------------------ */

import { toJalaali, toGregorian, jalaaliMonthLength, isValidJalaaliDate } from 'jalaali-js'

/** Timezone سازمان — قابل تنظیم از Settings هسته */
let organizationTimezone = 'Asia/Tehran'

export function setOrganizationTimezone(tz: string): void {
  organizationTimezone = tz
}

export function getOrganizationTimezone(): string {
  return organizationTimezone
}

export const JALALI_MONTHS = [
  'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
  'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند',
] as const

export const JALALI_WEEKDAYS = [
  'شنبه', 'یک‌شنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه',
] as const

export const JALALI_WEEKDAYS_SHORT = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'] as const

export interface JalaliDate {
  jy: number
  jm: number
  jd: number
}

/* ------------------------------------------------------------------
   کمکی‌های داخلی
   ------------------------------------------------------------------ */

function parse(input: string | Date | null | undefined): Date | null {
  if (input == null || input === '') return null
  const d = input instanceof Date ? input : new Date(input)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * اجزای تاریخ/زمان را در Timezone سازمان استخراج می‌کند.
 * از Intl استفاده می‌شود تا انتقال ساعت تابستانی و اختلاف نیم‌ساعته
 * ایران به‌درستی اعمال شود — محاسبه دستی offset اشتباه است.
 */
function partsInOrgTz(date: Date): {
  year: number; month: number; day: number
  hour: number; minute: number; second: number
} {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: organizationTimezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  })

  const out: Record<string, number> = {}
  for (const { type, value } of fmt.formatToParts(date)) {
    if (type !== 'literal') out[type] = Number(value)
  }

  // ساعت ۲۴ در برخی محیط‌ها به‌جای ۰ برگردانده می‌شود
  return {
    year: out.year, month: out.month, day: out.day,
    hour: out.hour === 24 ? 0 : out.hour,
    minute: out.minute, second: out.second,
  }
}

const pad = (n: number): string => String(n).padStart(2, '0')

/* ------------------------------------------------------------------
   ارقام فارسی
   ------------------------------------------------------------------ */

const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']

/** ارقام لاتین را به فارسی تبدیل می‌کند — فقط برای نمایش */
export function toPersianDigits(value: string | number): string {
  return String(value).replace(/\d/g, (d) => PERSIAN_DIGITS[Number(d)])
}

/** ارقام فارسی/عربی را به لاتین برمی‌گرداند — برای ارسال به Backend */
export function toLatinDigits(value: string): string {
  return value
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
}

/* ------------------------------------------------------------------
   تبدیل
   ------------------------------------------------------------------ */

/** ISO/UTC → اجزای تاریخ شمسی در Timezone سازمان */
export function toJalali(input: string | Date | null | undefined): JalaliDate | null {
  const d = parse(input)
  if (!d) return null
  const p = partsInOrgTz(d)
  return toJalaali(p.year, p.month, p.day)
}

/**
 * تاریخ شمسی + ساعت محلی → رشته ISO-8601 UTC.
 * این تنها مسیر مجاز برای فرستادن تاریخ به Backend است.
 */
export function jalaliToUtcIso(
  jy: number, jm: number, jd: number,
  hour = 0, minute = 0, second = 0,
): string {
  const { gy, gm, gd } = toGregorian(jy, jm, jd)

  // زمان محلی سازمان را به UTC نگاشت می‌کنیم: ابتدا یک حدس UTC می‌سازیم،
  // سپس اختلاف واقعی آن حدس در Timezone سازمان را کم می‌کنیم.
  const guess = Date.UTC(gy, gm - 1, gd, hour, minute, second)
  const p = partsInOrgTz(new Date(guess))
  const asOrg = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return new Date(guess - (asOrg - guess)).toISOString()
}

/* ------------------------------------------------------------------
   قالب‌بندی نمایش
   ------------------------------------------------------------------ */

export interface FormatOptions {
  /** ارقام فارسی — پیش‌فرض true */
  persianDigits?: boolean
  /** متن جایگزین وقتی مقدار خالی است — پیش‌فرض «—» */
  fallback?: string
}

/** ۱۴۰۵/۰۵/۰۹ */
export function formatJalali(
  input: string | Date | null | undefined,
  options: FormatOptions = {},
): string {
  const { persianDigits = true, fallback = '—' } = options
  const j = toJalali(input)
  if (!j) return fallback
  const s = `${j.jy}/${pad(j.jm)}/${pad(j.jd)}`
  return persianDigits ? toPersianDigits(s) : s
}

/** ۹ مرداد ۱۴۰۵ */
export function formatJalaliLong(
  input: string | Date | null | undefined,
  options: FormatOptions = {},
): string {
  const { persianDigits = true, fallback = '—' } = options
  const j = toJalali(input)
  if (!j) return fallback
  const s = `${j.jd} ${JALALI_MONTHS[j.jm - 1]} ${j.jy}`
  return persianDigits ? toPersianDigits(s) : s
}

/** پنج‌شنبه ۹ مرداد ۱۴۰۵ */
export function formatJalaliWithWeekday(
  input: string | Date | null | undefined,
  options: FormatOptions = {},
): string {
  const { persianDigits = true, fallback = '—' } = options
  const d = parse(input)
  const j = toJalali(input)
  if (!d || !j) return fallback
  const s = `${weekdayName(d)} ${j.jd} ${JALALI_MONTHS[j.jm - 1]} ${j.jy}`
  return persianDigits ? toPersianDigits(s) : s
}

/** ۱۴:۳۰ */
export function formatTime(
  input: string | Date | null | undefined,
  options: FormatOptions & { withSeconds?: boolean } = {},
): string {
  const { persianDigits = true, fallback = '—', withSeconds = false } = options
  const d = parse(input)
  if (!d) return fallback
  const p = partsInOrgTz(d)
  const s = withSeconds
    ? `${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}`
    : `${pad(p.hour)}:${pad(p.minute)}`
  return persianDigits ? toPersianDigits(s) : s
}

/** ۱۴۰۵/۰۵/۰۹ — ۱۴:۳۰ */
export function formatJalaliDateTime(
  input: string | Date | null | undefined,
  options: FormatOptions & { withSeconds?: boolean } = {},
): string {
  const { fallback = '—' } = options
  const d = parse(input)
  if (!d) return fallback
  return `${formatJalali(d, options)} — ${formatTime(d, options)}`
}

/** نام روز هفته بر مبنای Timezone سازمان (شنبه = ۰) */
export function weekdayName(input: string | Date | null | undefined): string {
  const d = parse(input)
  if (!d) return ''
  const name = new Intl.DateTimeFormat('en-US', {
    timeZone: organizationTimezone,
    weekday: 'short',
  }).format(d)
  const index = ['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'].indexOf(name)
  return index >= 0 ? JALALI_WEEKDAYS[index] : ''
}

/** «۳ روز پیش» / «۲ ساعت دیگر» */
export function formatRelative(
  input: string | Date | null | undefined,
  options: FormatOptions & { now?: Date } = {},
): string {
  const { persianDigits = true, fallback = '—', now = new Date() } = options
  const d = parse(input)
  if (!d) return fallback

  const diffSec = Math.round((d.getTime() - now.getTime()) / 1000)
  const abs = Math.abs(diffSec)

  const units: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, 'second'],
    [3600, 'minute'],
    [86400, 'hour'],
    [604800, 'day'],
    [2629800, 'week'],
    [31557600, 'month'],
    [Infinity, 'year'],
  ]
  const divisors = [1, 60, 3600, 86400, 604800, 2629800, 31557600]

  let index = units.findIndex(([limit]) => abs < limit)
  if (index < 0) index = units.length - 1

  const value = Math.round(diffSec / divisors[index])
  const rtf = new Intl.RelativeTimeFormat('fa-IR', { numeric: 'auto' })
  const s = rtf.format(value, units[index][1])
  return persianDigits ? s : toLatinDigits(s)
}

/* ------------------------------------------------------------------
   کمکی‌های تقویم — برای Date Picker
   ------------------------------------------------------------------ */

export function jalaliMonthLength(jy: number, jm: number): number {
  return jalaaliMonthLength(jy, jm)
}

export function isValidJalali(jy: number, jm: number, jd: number): boolean {
  return isValidJalaaliDate(jy, jm, jd)
}

/** امروز به وقت سازمان */
export function todayJalali(): JalaliDate {
  const p = partsInOrgTz(new Date())
  return toJalaali(p.year, p.month, p.day)
}

/** شماره ستون روز اول ماه در جدول تقویم (شنبه = ۰) */
export function jalaliMonthStartWeekday(jy: number, jm: number): number {
  const { gy, gm, gd } = toGregorian(jy, jm, 1)
  const name = new Intl.DateTimeFormat('en-US', {
    timeZone: organizationTimezone,
    weekday: 'short',
  }).format(new Date(Date.UTC(gy, gm - 1, gd, 12)))
  const index = ['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'].indexOf(name)
  return index < 0 ? 0 : index
}

/**
 * تاریخ شمسی → رشته میلادیِ «YYYY-MM-DD».
 *
 * ==================================================================
 * 🔴 چرا `jalaliToUtcIso` برای این کار درست نیست:
 *
 * آن تابع رشته کاملِ ISO با ساعت می‌سازد. برای فیلدی که فقط «روز» را
 * می‌خواهد (مثل تاریخ شروع مرخصی)، تبدیل به UTC می‌تواند روز را یک
 * واحد جابه‌جا کند — کاربر ۱۵ مرداد می‌زند و ۱۴ مرداد ثبت می‌شود.
 *
 * اینجا هیچ منطقه زمانی‌ای دخالت نمی‌کند: تقویم شمسی مستقیم به تقویم
 * میلادی نگاشت می‌شود و همان روز می‌ماند.
 * ==================================================================
 */
export function jalaliToDateString(jy: number, jm: number, jd: number): string {
  const { gy, gm, gd } = toGregorian(jy, jm, jd)
  const pad = (n: number) => String(n).padStart(2, '0')

  return `${gy}-${pad(gm)}-${pad(gd)}`
}

/** رشته میلادیِ «YYYY-MM-DD» → اجزای تاریخ شمسی. */
export function dateStringToJalali(value: string | null | undefined): JalaliDate | null {
  if (!value) return null

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return null

  return toJalaali(Number(match[1]), Number(match[2]), Number(match[3]))
}

/** آیا دو زمان در یک روز شمسی هستند؟ */
export function isSameJalaliDay(
  a: string | Date | null | undefined,
  b: string | Date | null | undefined,
): boolean {
  const ja = toJalali(a)
  const jb = toJalali(b)
  if (!ja || !jb) return false
  return ja.jy === jb.jy && ja.jm === jb.jm && ja.jd === jb.jd
}

/* ------------------------------------------------------------------
   عدد و مبلغ
   ------------------------------------------------------------------ */

/** جداکننده هزارگان */
export function formatNumber(
  value: number | null | undefined,
  options: FormatOptions = {},
): string {
  const { persianDigits = true, fallback = '—' } = options
  if (value == null || Number.isNaN(value)) return fallback
  const s = new Intl.NumberFormat('en-US').format(value)
  return persianDigits ? toPersianDigits(s) : s
}

/**
 * مبلغ از واحد فرعی (ریال) — سند مادر بند ۱۵ مبلغ را
 * به‌صورت integer minor unit + currency ذخیره می‌کند.
 */
export function formatMoney(
  minorUnits: number | null | undefined,
  currency = 'IRR',
  options: FormatOptions = {},
): string {
  const { fallback = '—' } = options
  if (minorUnits == null || Number.isNaN(minorUnits)) return fallback
  const label = currency === 'IRR' ? 'ریال' : currency === 'IRT' ? 'تومان' : currency
  return `${formatNumber(minorUnits, options)} ${label}`
}

/* ------------------------------------------------------------------
   ورودیِ مبلغ — جداکنندهٔ هزارگان حینِ تایپ
   ------------------------------------------------------------------
   🔴 چرا اینجا و نه در هر فرم:

   هر فرمی که مبلغ می‌گرفت این جفت را خودش می‌نوشت — یک‌بار برای
   نمایش با جداکننده و یک‌بار برای پاک‌کردنش پیش از ارسال. و چون دو
   طرف جدا نوشته می‌شدند، می‌شد یکی را فارسی و دیگری را لاتین نوشت.

   ⚠️ دقیقاً همین در فرمِ مساعده افتاده بود: نمایش با
   `toLocaleString('fa-IR')` رقمِ **فارسی** می‌ساخت («۵۰٬۰۰۰٬۰۰۰») و
   خواندن با `/[^0-9]/g` فقط رقمِ **لاتین** را نگه می‌داشت. حاصلِ هر
   بار خواندن رشتهٔ خالی بود، پس مبلغ همیشه صفر می‌ماند و دکمهٔ ثبت
   هرگز فعال نمی‌شد.

   🔴 قاعده: این دو تابع همیشه با هم استفاده می‌شوند. هر فرمی که
   `formatAmountInput` را برای نمایش به کار می‌برد، باید با
   `parseAmountInput` بخواند — و برعکس.
   ------------------------------------------------------------------ */

/**
 * ارقامِ خامِ یک ورودیِ مبلغ — مستقل از زبانِ رقم و جداکننده.
 *
 * ⚠️ ارقامِ فارسی/عربی نخست لاتین می‌شوند، بعد هرچه رقم نیست حذف
 * می‌شود. پس هم «۵۰٬۰۰۰» و هم «50,000» به «50000» می‌رسند.
 *
 * @returns رشتهٔ ارقامِ لاتین؛ برای ورودیِ بی‌رقم، رشتهٔ خالی.
 */
export function amountDigits(value: string): string {
  /*
   | ⚠️ صفرهای ابتدایی حذف می‌شوند وگرنه «۰۰۵» با جداکننده «۰۰۵»
   | می‌ماند و کاربر عددی می‌بیند که خودش ننوشته.
   */
  const digits = toLatinDigits(value).replace(/\D/g, '').replace(/^0+(?=\d)/, '')

  return digits
}

/**
 * مقدارِ نمایشیِ یک ورودیِ مبلغ — با جداکنندهٔ هزارگان.
 *
 * ⚠️ رقم‌ها **لاتین** می‌مانند و فارسی نمی‌شوند.
 *
 * 🔴 چرا: کاربر داخلِ همین فیلد تایپ می‌کند و مکان‌نما جابه‌جا
 * می‌شود؛ رقمِ فارسی در فیلدِ `dir="ltr"` جهتِ ویرایش را به هم
 * می‌ریزد. نمایشِ فارسی جای خودش را دارد — زیرِ فیلد و در جدول‌ها،
 * با `formatMoney` — نه داخلِ کنترلِ در حالِ تایپ.
 */
export function formatAmountInput(value: string): string {
  const digits = amountDigits(value)
  if (digits === '') return ''

  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/**
 * عددِ یک ورودیِ مبلغ، برای ارسال به Backend.
 *
 * ⚠️ برای ورودیِ خالی `0` برمی‌گرداند و نه `NaN` — فرم‌ها با
 * `> 0` می‌سنجند و `NaN` هر مقایسه‌ای را خاموش رد می‌کند.
 */
export function parseAmountInput(value: string): number {
  const digits = amountDigits(value)

  return digits === '' ? 0 : Number(digits)
}
